import { useState } from 'react';
import { useNavigate, useRevalidator } from 'react-router';
import { Button } from '@/components/ui/button';
import { api, errMsg } from '@/lib/api';

export function EvaluateButton({ id }: { id: string }) {
  const { revalidate } = useRevalidator();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  async function evaluate() {
    setBusy(true);
    setResult(null);
    try {
      const r = await api<{ entered: number; exited: number; memberCount: number }>(`/segments/${id}/evaluate`, { method: 'POST' });
      setResult(`${r.memberCount} (+${r.entered} / −${r.exited})`);
      revalidate();
    } catch (err) {
      setResult(errMsg(err, 'failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="flex items-center gap-2">
      <Button size="sm" variant="outline" onClick={evaluate} disabled={busy}>
        {busy ? 'Evaluating…' : 'Evaluate now'}
      </Button>
      {result && <span className="text-xs text-muted-foreground">{result}</span>}
    </span>
  );
}

export function DeleteSegmentButton({ id }: { id: string }) {
  const navigate = useNavigate();
  const { revalidate } = useRevalidator();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  async function remove() {
    setBusy(true);
    try {
      await api(`/segments/${id}`, { method: 'DELETE' });
      navigate('/segments');
      revalidate();
    } finally {
      setBusy(false);
    }
  }

  if (!confirm) {
    return (
      <Button size="sm" variant="outline" onClick={() => setConfirm(true)}>
        Delete segment
      </Button>
    );
  }
  return (
    <span className="flex items-center gap-2 text-xs">
      <span>Remove this segment from all contacts?</span>
      <Button size="sm" variant="destructive" onClick={remove} disabled={busy}>
        Yes, delete
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setConfirm(false)}>
        Cancel
      </Button>
    </span>
  );
}

export function ImportCsv({ segmentId }: { segmentId: string }) {
  const { revalidate } = useRevalidator();
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  async function upload() {
    setBusy(true);
    setResult(null);
    try {
      const r = await api<{ created: number; updated: number; skipped: number }>(`/segments/${segmentId}/import`, { method: 'POST', json: { csv } });
      setResult(`Imported: ${r.created} new, ${r.updated} updated, ${r.skipped} skipped`);
      setCsv('');
      revalidate();
    } catch (err) {
      setResult(errMsg(err, 'Import failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2 rounded-lg border bg-white p-4 text-sm">
      <h2 className="text-base">Import leads (CSV)</h2>
      <p className="text-xs text-muted-foreground">
        Columns: <code>email, first_name, last_name, business_name, tags</code> (tags separated by <code>;</code>). Existing contacts are
        merged by email; new ones are created as leads.
      </p>
      <input
        type="file"
        accept=".csv,text/csv"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file) setCsv(await file.text());
        }}
      />
      <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={6} className="w-full rounded-md border p-2 font-mono text-xs" placeholder="email,first_name,last_name,business_name,tags" />
      <Button size="sm" onClick={upload} disabled={busy || !csv.trim()}>
        {busy ? 'Importing…' : 'Import'}
      </Button>
      {result && <p className="text-xs text-muted-foreground">{result}</p>}
    </div>
  );
}
